import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useSetupProject } from "@/hooks/useSetupProject";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  FileText,
  Layers,
  Library,
  Loader2
} from "lucide-react";
import React, { useState } from "react";

interface GuidedProjectCreationProps {
  open: boolean;
  onClose: () => void;
  onComplete: (projectId: string) => void;
}

interface ProjectFormData {
  name: string;
  description: string;
}

interface StoryFormData {
  name: string;
  description: string;
}

interface TemplateCategory {
  id: string;
  name: string;
  description: string;
  selected: boolean;
}

type ProjectType = 'single' | 'multi' | null;

export const GuidedProjectCreation: React.FC<GuidedProjectCreationProps> = ({
  open,
  onClose,
  onComplete
}) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Project Type
  const [projectType, setProjectType] = useState<ProjectType>(null);

  // Form data
  const [projectData, setProjectData] = useState<ProjectFormData>({
    name: "",
    description: ""
  });

  const [storyData, setStoryData] = useState<StoryFormData>({
    name: "",
    description: ""
  });

  const [templateCategories, setTemplateCategories] = useState<TemplateCategory[]>([
    {
      id: "characters",
      name: "Characters",
      description: "Templates for character development and profiles",
      selected: true
    },
    {
      id: "places",
      name: "Places",
      description: "Templates for locations, settings, and world-building",
      selected: true
    },
    {
      id: "items",
      name: "Items",
      description: "Templates for important objects, artifacts, and props",
      selected: false
    },
    {
      id: "events",
      name: "Events",
      description: "Templates for key events, scenes, and plot points",
      selected: false
    }
  ]);

  // Created data IDs
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);

  // Hooks
  const { toast } = useToast();
  const setupProject = useSetupProject();

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setProjectType(null);
      setProjectData({ name: "", description: "" });
      setStoryData({ name: "", description: "" });
      setCreatedProjectId(null);
      setIsProcessing(false);
    }
  }, [open]);

  // Handle project form changes
  const handleProjectChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProjectData(prev => ({ ...prev, [name]: value }));
  };

  // Handle story form changes
  const handleStoryChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setStoryData(prev => ({ ...prev, [name]: value }));
  };

  // Toggle template category selection
  const toggleCategory = (id: string) => {
    setTemplateCategories(prev =>
      prev.map(category =>
        category.id === id
          ? { ...category, selected: !category.selected }
          : category
      )
    );
  };

  // Handle next step
  const handleNext = async () => {
    // Validate current step
    if (currentStep === 0) {
      if (!projectType) {
        toast({
          title: "Selection required",
          description: "Please select if this is a single story or multi-story project",
          variant: "destructive"
        });
        return;
      }
      if (!projectData.name.trim()) {
        toast({
          title: "Project name required",
          description: "Please enter a name for your project",
          variant: "destructive"
        });
        return;
      }
    } else if (currentStep === 1) {
      // Validation for Story Details step (only for multi-story)
      if (!storyData.name.trim()) {
        toast({
          title: "Story name required",
          description: "Please enter a name for your story",
          variant: "destructive"
        });
        return;
      }
    }

    // Determine next step index
    let nextStepIndex = currentStep + 1;

    // Skip Story Details step if Single Story
    if (currentStep === 0 && projectType === 'single') {
      nextStepIndex = 2; // Jump to Templates
      // Auto-populate story data from project data
      setStoryData({
        name: projectData.name,
        description: projectData.description
      });
    }

    // If we are at the completion step (logic handled in button click, but safety check here)
    if (currentStep === 2) {
      await completeSetup();
      return;
    }

    setCurrentStep(nextStepIndex);
  };

  // Handle back step
  const handleBack = () => {
    if (currentStep === 2 && projectType === 'single') {
      setCurrentStep(0); // Jump back to start from templates if single story
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Complete the setup process
  const completeSetup = async () => {
    setIsProcessing(true);

    try {
      // Get selected categories
      const selectedCategories = templateCategories.filter(cat => cat.selected);

      // Use the setup project hook to create everything in one go
      const result = await setupProject.mutateAsync({
        projectName: projectData.name,
        projectDescription: projectData.description,
        storyName: storyData.name,
        storyDescription: storyData.description,
        categories: selectedCategories.map(cat => cat.name)
      });

      // Save the created IDs
      setCreatedProjectId(result.project.id);

      // Move to completion step
      setCurrentStep(3);

    } catch (error) {
      console.error("Error during project setup:", error);
      toast({
        title: "Error creating project",
        description: "There was a problem setting up your project. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle completion
  const handleComplete = () => {
    if (createdProjectId) {
      onComplete(createdProjectId);
    }
    onClose();
  };

  // Handle dialog close
  const handleDialogClose = (open: boolean) => {
    if (!open) {
      // Only allow closing if not processing
      if (!isProcessing) {
        onClose();
      }
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6 px-1">
            <div className="space-y-4">
              <Label className="text-base font-semibold">Project Structure</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={cn(
                    "cursor-pointer rounded-xl border-2 p-4 transition-all hover:border-primary/50 hover:bg-muted/50",
                    projectType === 'single' ? "border-primary bg-primary/5" : "border-muted"
                  )}
                  onClick={() => setProjectType('single')}
                >
                  <div className="mb-3 rounded-full bg-primary/10 w-10 h-10 flex items-center justify-center text-primary">
                    <BookOpen className="h-5 w-5" />
                  </div>
                  <h4 className="font-semibold mb-1">Single Story</h4>
                  <p className="text-sm text-muted-foreground">
                    A standalone novel or short story. The project is the story.
                  </p>
                </div>

                <div
                  className={cn(
                    "cursor-pointer rounded-xl border-2 p-4 transition-all hover:border-primary/50 hover:bg-muted/50",
                    projectType === 'multi' ? "border-primary bg-primary/5" : "border-muted"
                  )}
                  onClick={() => setProjectType('multi')}
                >
                  <div className="mb-3 rounded-full bg-primary/10 w-10 h-10 flex items-center justify-center text-primary">
                    <Library className="h-5 w-5" />
                  </div>
                  <h4 className="font-semibold mb-1">Multi-Story / Series</h4>
                  <p className="text-sm text-muted-foreground">
                    A collection of stories, an anthology, or a book series.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <div className="space-y-2.5">
                <Label htmlFor="name" className="text-sm font-medium">Project Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={projectData.name}
                  onChange={handleProjectChange}
                  placeholder={projectType === 'single' ? "e.g., The Great Gatsby" : "e.g., The Lord of the Rings Series"}
                  className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20"
                />
              </div>

              <div className="space-y-2.5">
                <Label htmlFor="description" className="text-sm font-medium">Description (Optional)</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={projectData.description}
                  onChange={handleProjectChange}
                  placeholder="Briefly describe what this project is about..."
                  rows={3}
                  className="min-h-[80px] rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 resize-none"
                />
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6 px-1">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 border border-border/40">
              <div className="h-10 w-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-medium text-sm">First Story Details</h4>
                <p className="text-xs text-muted-foreground">This is the first story in your "{projectData.name}" collection.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2.5">
                <Label htmlFor="storyName" className="text-sm font-medium">Story Name</Label>
                <Input
                  id="storyName"
                  name="name"
                  value={storyData.name}
                  onChange={handleStoryChange}
                  placeholder="e.g., The Fellowship of the Ring"
                  className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20"
                />
              </div>

              <div className="space-y-2.5">
                <Label htmlFor="storyDescription" className="text-sm font-medium">Description (Optional)</Label>
                <Textarea
                  id="storyDescription"
                  name="description"
                  value={storyData.description}
                  onChange={handleStoryChange}
                  placeholder="A brief summary of this specific story..."
                  rows={3}
                  className="min-h-[80px] rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 resize-none"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 px-1">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 border border-border/40">
              <div className="h-10 w-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-medium text-sm">In-Story Templates</h4>
                <p className="text-xs text-muted-foreground">Select templates to start with. You can always add or modify these later.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {templateCategories.map(category => (
                <div
                  key={category.id}
                  onClick={() => toggleCategory(category.id)}
                  className={cn(
                    "cursor-pointer flex items-start space-x-3 p-3 rounded-xl border transition-all",
                    category.selected
                      ? "border-primary/50 bg-primary/5 shadow-sm"
                      : "border-border/60 hover:border-primary/30 hover:bg-muted/30"
                  )}
                >
                  <Checkbox
                    id={`category-${category.id}`}
                    checked={category.selected}
                    onCheckedChange={() => toggleCategory(category.id)}
                    className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                  />
                  <div className="space-y-1">
                    <label
                      htmlFor={`category-${category.id}`}
                      className="text-sm font-medium cursor-pointer block leading-none"
                    >
                      {category.name}
                    </label>
                    <p className="text-xs text-muted-foreground leading-snug">
                      {category.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 3: // Completion step
        return (
          <div className="space-y-8 py-8 px-4 flex flex-col items-center justify-center text-center animate-in fade-in zoom-in duration-300">
            <div className="h-20 w-20 rounded-full bg-emerald-500/10 flex items-center justify-center ring-8 ring-emerald-500/5">
              <Check className="h-10 w-10 text-emerald-600" />
            </div>

            <div className="space-y-2 max-w-xs mx-auto">
              <h3 className="text-2xl font-semibold tracking-tight">Project Ready!</h3>
              <p className="text-muted-foreground">
                "{projectData.name}" has been successfully created.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Stepper logic
  const steps = projectType === 'single'
    ? ["Project Details", "Templates", "Complete"]
    : ["Project Details", "Story Details", "Templates", "Complete"];

  // Adjust displayed current step index based on flow
  const displayStepIndex = projectType === 'single' && currentStep >= 2
    ? currentStep - 1
    : currentStep;

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
        <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
          <DialogTitle className="text-2xl font-semibold tracking-tight">Create New Project</DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            {currentStep < 3 ? "Set up your workspace in a few simple steps." : "Your workspace is ready."}
          </DialogDescription>
        </DialogHeader>

        <div className="p-8 pb-0">
          {currentStep < 3 && (
            <div className="flex items-center gap-2 mb-8">
              {steps.slice(0, -1).map((_, index) => (
                <div key={index} className="flex items-center gap-2 flex-1">
                  <div className={cn(
                    "h-2 rounded-full flex-1 transition-all duration-500",
                    index <= displayStepIndex
                      ? "bg-primary"
                      : "bg-muted"
                  )} />
                </div>
              ))}
            </div>
          )}

          <div className="min-h-[300px]">
            {renderStepContent()}
          </div>
        </div>

        <DialogFooter className="p-8 pt-4 flex items-center justify-between sm:justify-between">
          <div className="flex items-center">
            {currentStep > 0 && currentStep < 3 && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleBack}
                disabled={isProcessing}
                className="hover:bg-muted/50 rounded-xl"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
          </div>

          {currentStep < 3 ? (
            <Button
              type="button"
              onClick={handleNext}
              disabled={isProcessing}
              className="rounded-xl px-6 font-medium shadow-lg shadow-primary/20"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : currentStep === 2 ? (
                <>
                  Create Project
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleComplete}
              className="w-full rounded-xl py-6 text-lg font-medium shadow-lg shadow-primary/20"
            >
              Enter Project
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
